import { Asset } from 'contentful';

type ContentfulImageOptions = {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb';
  format?: 'webp' | 'jpg' | 'png';
};

const getAssetUrl = (asset?: Asset) => {
  const url = asset?.fields.file.url;

  if (!url) {
    return '';
  }

  return getSafeImageUrl(url);
};

export const getSafeImageUrl = (url?: string) => {
  if (!url) {
    return '';
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }

  return url;
};

export const getContentfulImageUrl = (asset?: Asset, options: ContentfulImageOptions = {}) => {
  const assetUrl = getAssetUrl(asset);

  if (!assetUrl) {
    return '';
  }

  const params = new URLSearchParams();
  const { width, height, quality = 78, fit = 'scale', format = 'webp' } = options;

  if (width) {
    params.set('w', String(width));
  }

  if (height) {
    params.set('h', String(height));
  }

  if (width || height) {
    params.set('fit', fit);
  }

  if (quality) {
    params.set('q', String(quality));
  }

  if (format) {
    params.set('fm', format);
  }

  const query = params.toString();

  return query ? `${assetUrl}${assetUrl.includes('?') ? '&' : '?'}${query}` : assetUrl;
};

export const getContentfulImageSrcSet = (
  asset: Asset | undefined,
  widths: number[],
  options: Omit<ContentfulImageOptions, 'width'> = {}
) =>
  widths
    .map((width) => {
      const url = getContentfulImageUrl(asset, { ...options, width });
      return url ? `${url} ${width}w` : '';
    })
    .filter(Boolean)
    .join(', ');
